import { Core_Sex, Patientportal_PortalUserMsg } from '@/generated/graphql';
import {
  BankOutlined,
  CalendarOutlined,
  LockOutlined,
  MailOutlined,
  NumberOutlined,
  PhoneOutlined,
  SafetyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { ChangeInsuranceInfoModal } from '@components/change-insurance-info-modal';
import { Button, Col, List, Row, Space, Typography } from 'antd';
import React, { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { buildFullName, buildTimestampDate } from '../../helpers/formatters';
import { useSimpleHealthInsuranceTypeFormatter } from '../../helpers/enum-formatters/useSimpleHealthInsuranceTypeFormatter';
import { formatAccountPhoneNumber, useCurrentUser } from '../../helpers/utils';
import styles from '../../pages/user/user.module.scss';
import { ChangeBirthDateModal } from '../change-birth-date-modal';
import { ChangeNameModal } from '../change-name-modal';
import { ChangePasswordModal } from '../change-password-modal';
import { ChangePhoneNumberModal } from '../change-phone-number-modal';
import { SexIcon } from '../common-layout/sex-icon';

const { Title } = Typography;

interface PortalUserListItem {
  id: string;
  icon: React.ReactNode;
  content: React.ReactNode;
  onClick?: () => void;
}

interface PortalUserListGroup {
  key: string;
  title: string;
  items: PortalUserListItem[];
}

interface UserDetailsProps {
  userOverride?: Patientportal_PortalUserMsg;
  reduced?: boolean;
  hidePassword?: boolean;
  editable?: boolean;
}

export const UserDetails: FC<UserDetailsProps> = ({
  userOverride,
  reduced,
  hidePassword,
  editable = false,
}) => {
  const { formatMessage } = useIntl();
  const { formatSimpleHealthInsuranceType } = useSimpleHealthInsuranceTypeFormatter();

  const {
    currentUser,
    userLoading: currentUserLoading,
    refetch: refetchCurrentUser,
  } = useCurrentUser();
  const user = userOverride ?? currentUser;

  const [changePasswordOpen, setChangePasswordOpen] = useState<boolean>(false);
  const [changeNameOpen, setChangeNameOpen] = useState<boolean>(false);
  const [changeBirthDateOpen, setChangeBirthDateOpen] = useState<boolean>(false);
  const [changePhoneNumberOpen, setChangePhoneNumberOpen] = useState<boolean>(false);
  const [changeInsuranceOpen, setChangeInsuranceOpen] = useState<boolean>(false);

  const persona: PortalUserListItem[] = [
    {
      id: 'user.name',
      icon: <UserOutlined />,
      content: (
        <Space direction="vertical" size={0}>
          <Space size={5}>
            {user?.sex && user?.sex !== Core_Sex.SexNone && <SexIcon sex={user?.sex} />}
            {buildFullName(user?.name)}
          </Space>
          {editable && (
            <Button
              type="link"
              style={{ margin: 0, padding: 0, textAlign: 'left' }}
              onClick={() => setChangeNameOpen(true)}
            >
              {formatMessage({ id: 'user.name.description' })}
            </Button>
          )}
        </Space>
      ),
    },
    {
      id: 'user.birthday',
      icon: <CalendarOutlined />,
      content: (
        <Space direction="vertical" size={0}>
          <span>{buildTimestampDate(user?.dateOfBirth)}</span>
          {editable && (
            <Button
              type="link"
              style={{ margin: 0, padding: 0, textAlign: 'left' }}
              onClick={() => setChangeBirthDateOpen(true)}
            >
              {formatMessage({ id: 'user.birthday.description' })}
            </Button>
          )}
        </Space>
      ),
    },
    {
      id: 'user.password',
      icon: <LockOutlined />,
      content: (
        <Button
          type="link"
          style={{
            margin: 0,
            padding: 0,
            textAlign: 'left',
          }}
          onClick={() => setChangePasswordOpen(true)}
        >
          {formatMessage({ id: 'user.password.description' })}
        </Button>
      ),
    },
  ];

  const accountPhoneNumber = formatAccountPhoneNumber({
    phoneNumber: user?.phoneNumber,
    countryCode: user?.phoneNumberCountryCode,
  });

  const contact: PortalUserListItem[] = [
    {
      id: 'user.email',
      icon: <MailOutlined />,
      content: (
        <Space direction="vertical" size={0}>
          <span>{user?.email}</span>
          {editable && (
            <Button
              type="link"
              style={{ margin: 0, padding: 0, textAlign: 'left', height: 'auto' }}
              href="https://www.avelios.com/de/contact"
              target="_blank"
            >
              {formatMessage({ id: 'user.email.change-notice' })}
            </Button>
          )}
        </Space>
      ),
    },
    {
      id: 'user.phone',
      icon: <PhoneOutlined />,
      content: (
        <Space direction="vertical" size={0}>
          <span>{accountPhoneNumber || '-'}</span>
          {editable && (
            <Button
              type="link"
              style={{ margin: 0, padding: 0, textAlign: 'left' }}
              onClick={() => setChangePhoneNumberOpen(true)}
            >
              {formatMessage({ id: 'user.phone.description' })}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const insuranceNumber = user?.insuranceInfo?.insuranceNumber;
  const insurerNumber = user?.insuranceInfo?.insurerNumber;

  const insurance: PortalUserListItem[] = [
    {
      id: 'user.insurance-type',
      icon: <SafetyOutlined />,
      content: (
        <div>
          <div>{formatSimpleHealthInsuranceType(user?.insuranceInfo?.insuranceType)}</div>
          {editable && (
            <Button
              type="link"
              style={{
                padding: 0,
              }}
              onClick={() => setChangeInsuranceOpen(true)}
            >
              {formatMessage({ id: 'user.insurance-type-change' })}
            </Button>
          )}
        </div>
      ),
    },
    {
      id: 'user.insurance-number',
      icon: <NumberOutlined />,
      content: (
        <div>
          <div>{insuranceNumber || '-'}</div>
          {editable && (
            <Button
              type="link"
              style={{
                padding: 0,
              }}
              onClick={() => setChangeInsuranceOpen(true)}
            >
              {formatMessage({
                id: insuranceNumber
                  ? 'user.insurance-number-change'
                  : 'user.insurance-number-add',
              })}
            </Button>
          )}
        </div>
      ),
    },
    {
      id: 'user.insurer-number',
      icon: <BankOutlined />,
      content: (
        <div>
          <div>{insurerNumber || '-'}</div>
          {editable && (
            <Button
              type="link"
              style={{
                padding: 0,
              }}
              onClick={() => setChangeInsuranceOpen(true)}
            >
              {formatMessage({
                id: insurerNumber
                  ? 'user.insurer-number-change'
                  : 'user.insurer-number-add',
              })}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const groups: PortalUserListGroup[] = [
    {
      key: 'user',
      title: formatMessage({ id: 'user.persona' }),
      items: persona,
    },
    {
      key: 'contact',
      title: formatMessage({ id: 'user.contact' }),
      items: contact,
    },
    {
      key: 'insurance',
      title: formatMessage({ id: 'user.insurance' }),
      items: insurance,
    },
  ];

  const hiddenGroups = new Set<string>(reduced ? ['contact', 'insurance'] : []);
  const hiddenItems = new Set<string>(
    reduced || !editable || hidePassword ? ['user.password'] : [],
  );

  const groupIsVisible = (key: string) => !hiddenGroups.has(key);
  const itemIsVisible = (id: string) => !hiddenItems.has(id);

  return (
    <div>
      <Space style={{ width: '100%' }} direction={'vertical'} size={8}>
        {groups
          ?.filter((group) => groupIsVisible(group.key))
          ?.map((group) => (
            <div key={group.key} className={styles.group}>
              <Row>
                <Col span={24}>
                  <Title level={3} style={{ fontWeight: 'bold' }}>
                    {group.title}
                  </Title>
                </Col>
              </Row>
              <List
                dataSource={group.items?.filter((item) => itemIsVisible(item.id))}
                renderItem={(item) => (
                  <List.Item
                    key={item.id}
                    onClick={item.onClick}
                    className={item.onClick ? styles.listItemSelectable : undefined}
                  >
                    <List.Item.Meta
                      avatar={item.icon}
                      title={<b>{formatMessage({ id: item.id })}</b>}
                      description={item.content}
                    />
                  </List.Item>
                )}
              />
            </div>
          ))}
      </Space>
      {changePasswordOpen && (
        <ChangePasswordModal
          currentUser={user}
          currentUserLoading={currentUserLoading}
          refetchCurrentUser={refetchCurrentUser}
          open={changePasswordOpen}
          onCancel={() => setChangePasswordOpen(false)}
        />
      )}
      {changeNameOpen && (
        <ChangeNameModal
          currentUser={user}
          currentUserLoading={currentUserLoading}
          open={changeNameOpen}
          onClose={() => setChangeNameOpen(false)}
        />
      )}
      {changeBirthDateOpen && (
        <ChangeBirthDateModal
          currentUser={user}
          currentUserLoading={currentUserLoading}
          open={changeBirthDateOpen}
          onClose={() => setChangeBirthDateOpen(false)}
        />
      )}
      {changePhoneNumberOpen && (
        <ChangePhoneNumberModal
          currentUser={user}
          currentUserLoading={currentUserLoading}
          open={changePhoneNumberOpen}
          onClose={() => setChangePhoneNumberOpen(false)}
        />
      )}
      {changeInsuranceOpen && (
        <ChangeInsuranceInfoModal
          currentUser={user}
          refetchCurrentUser={refetchCurrentUser}
          onClose={() => setChangeInsuranceOpen(false)}
          open={changeInsuranceOpen}
          onCancel={() => setChangeInsuranceOpen(false)}
        />
      )}
    </div>
  );
};
